import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import {
  booleanScheme,
  momentScheme,
  numberScheme,
  passwordScheme,
  stringScheme,
} from 'src/utils/validation-schemes';
import Input from 'src/components/Input/Input';
import { Button } from 'src/components/ui/button';
import { FRESHDESK_URL } from 'src/constants/urls';
import { useUpdateKnowledgeBaseCollectorMutation } from 'src/services/knowledgeBaseService';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import moment from 'moment';
import { Switch } from 'src/components/ui/switch';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { TICKETS_INTERVAL } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import { ISO_DATE_FORMAT } from 'src/shared/constants';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { useAppSelector } from 'src/store';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';

export const EditKnowledgeBaseFreshdeskTicketsForm = ({
  item,
  knowledgeBaseId,
  close = () => {},
}) => {
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const [
    updateCollector,
    { isLoading: isLoadingUpdateCollector, isSuccess: isSuccessUpdateCollector },
  ] = useUpdateKnowledgeBaseCollectorMutation();
  const { isInactivePlan, isTrialPlan } = useAppSelector((s) => s.planSlice);

  useEffect(() => {
    if (isSuccessUpdateCollector) {
      notification.success(`${item.name} Settings Updated Successfully.`);
      close();
    }
  }, [isSuccessUpdateCollector]);

  useEffect(() => {
    if (isLoadingUpdateCollector) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingUpdateCollector]);

  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty, isValid },
  } = useForm({
    defaultValues: {
      subdomain: item.data.subdomain,
      api_key: '',
      start_from: moment(item.data.start_from).format('MM/DD/YYYY'),
      autoSync: item.data.schedule.enabled,
      interval: item.data.schedule.interval,
    },
    resolver: yupResolver(
      object({
        subdomain: stringScheme({ required: true }).label('Freshdesk API Subdomain'),
        api_key: passwordScheme({ required: false, min: 3, max: 100 }).label('Freshdesk API Key'),
        start_from: momentScheme({ required: true, valid: true }),
        autoSync: booleanScheme({ required: false }).label('AutoSync'),
        interval: numberScheme({ required: false }).label('Interval'),
      }),
    ),
  });

  const submit = (data) => {
    if (isDirty && isValid) {
      const body = {
        ...item.data,
        schedule: { ...item.data.schedule },
      };

      body.start_from = moment(data.start_from).startOf('day').format(ISO_DATE_FORMAT);
      body.schedule.enabled = data.autoSync;
      body.schedule.interval = data.interval;
      body.subdomain = data.subdomain;

      if (data.api_key) {
        body.api_key = data.api_key;
      }

      updateCollector({ knowledgeBaseId: knowledgeBaseId || id, collectorId: item.id, body });
    }
  };

  const isAutoSync = watch('autoSync');

  return (
    <form
      onSubmit={handleSubmit(submit)}
      id="edit-freshdesk-tickets-integration"
      className={'h-full flex flex-col justify-between'}
    >
      <div className="flex flex-col gap-5 max-h-[510px] overflow-auto">
        <div className={'flex flex-col gap-2.5'}>
          <div className={'flex flex-col gap-2.5'}>
            <p className={'text-sm text-ebony_opacity_80 font-semibold'}>Synced Tickets</p>
          </div>
          <div className={'flex flex-col gap-2'}>
            <div className={'flex flex-row gap-1 items-center'}>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                >
                  <path
                    d="M2 6.23077H13.0769M2 6.23077V3.15385H13.0769V6.23077M2 6.23077V13H13.0769V6.23077M4.46154 3.15385V1M10.6154 3.15385V1"
                    stroke="#05050D"
                    strokeOpacity="0.3"
                    strokeWidth="1.25"
                  />
                </svg>
              </div>
              <p className={'text-sm text-ebony_opacity_30 font-medium'}>Solved from: </p>
              <p className={'text-sm text-ebony_opacity_80 font-medium'}>
                {moment(item.data.start_from).format('MM/DD/YYYY')}
              </p>
            </div>

            <div className={'flex flex-row gap-1 items-center'}>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                >
                  <path
                    d="M7.5 3.375C8.25625 3.375 8.875 3.99375 8.875 4.75C8.875 5.50625 8.25625 6.125 7.5 6.125C6.74375 6.125 6.125 5.50625 6.125 4.75C6.125 3.99375 6.74375 3.375 7.5 3.375ZM7.5 10.25C9.35625 10.25 11.4875 11.1369 11.625 11.625H3.375C3.53313 11.13 5.65062 10.25 7.5 10.25ZM7.5 2C5.98062 2 4.75 3.23062 4.75 4.75C4.75 6.26937 5.98062 7.5 7.5 7.5C9.01937 7.5 10.25 6.26937 10.25 4.75C10.25 3.23062 9.01937 2 7.5 2ZM7.5 8.875C5.66437 8.875 2 9.79625 2 11.625V13H13V11.625C13 9.79625 9.33563 8.875 7.5 8.875Z"
                    fill="#05050D"
                    fillOpacity="0.3"
                  />
                </svg>
              </div>
              <p className={'text-sm text-ebony_opacity_30 font-medium min-w-20'}>
                Solved by agents:{' '}
              </p>
              <p className={'text-sm text-ebony_opacity_80 font-medium'}>
                {item?.data?.agents?.length ? item.data.agents.join(', ') : 'all agents'}
              </p>
            </div>
            <div className={'flex flex-row gap-1 items-center'}>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M13.3333 6.66602V5.33268H10.6666V2.66602H9.33329V5.33268H6.66663V2.66602H5.33329V5.33268H2.66663V6.66602H5.33329V9.33268H2.66663V10.666H5.33329V13.3327H6.66663V10.666H9.33329V13.3327H10.6666V10.666H13.3333V9.33268H10.6666V6.66602H13.3333ZM9.33329 9.33268H6.66663V6.66602H9.33329V9.33268Z"
                    fill="#05050D"
                    fillOpacity="0.3"
                  />
                </svg>
              </div>
              <p className={'text-sm text-ebony_opacity_30 font-medium min-w-20'}>
                Solved with tags:{' '}
              </p>
              <p className={'text-sm text-ebony_opacity_80 font-medium'}>
                {item?.data?.tags?.length ? item.data.tags.join(', ') : 'all tags'}
              </p>
            </div>
            <div className={'flex flex-row gap-1 items-center'}>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M3.51221 9.85449H2.66064V12.8096H3.51221V14.0479H1.40674V8.61621H3.51221V9.85449ZM14.5923 13.21H4.81689V11.9707H14.5923V13.21ZM14.5923 10.6934H4.81689V9.45508H14.5923V10.6934ZM3.51221 3.18848H2.66064V6.14258H3.51221V7.38184H1.40674V1.94922H3.51221V3.18848ZM14.5923 6.54297H4.81689V5.30371H14.5923V6.54297ZM14.5923 4.02637H4.81689V2.78809H14.5923V4.02637Z"
                    fill="#05050D"
                    fillOpacity="0.3"
                  />
                </svg>
              </div>
              <p className={'text-sm text-ebony_opacity_30 font-medium min-w-20'}>
                Solved in groups:{' '}
              </p>
              <p className={'text-sm text-ebony_opacity_80 font-medium'}>
                {item?.data?.groups?.length ? item.data.groups.join(', ') : 'all groups'}
              </p>
            </div>
          </div>
          <div className={'flex flex-col gap-2.5'}>
            <div className={'flex flex-row gap-5 mt-2.5'}>
              <div>
                <Controller
                  control={control}
                  name="autoSync"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <span className="flex flex-row gap-4">
                      <span className="flex items-center gap-4">
                        <Switch
                          type="black"
                          checked={value}
                          onCheckedChange={onChange}
                          disabled={isTrialPlan || isInactivePlan}
                        />
                      </span>
                      <span
                        className={
                          'text-ebony_opacity_30 font-medium font-jamjuree text-sm flex flex-row items-center gap-2'
                        }
                      >
                        Auto-sync
                        {isTrialPlan && (
                          <InputInfoToolTip
                            infoToolTip={{
                              side: 'right',
                              description: <>Unavailable for trial plan</>,
                            }}
                          />
                        )}
                      </span>
                    </span>
                  )}
                />
              </div>
              {isAutoSync && (
                <div>
                  <Controller
                    control={control}
                    name="interval"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <RadioGroup
                        onChange={onChange}
                        onValueChange={onChange}
                        defaultValue={value as any}
                        className="flex flex-row gap-5 items-center"
                      >
                        <div className="flex items-center space-x-[7px] space-y-0">
                          <RadioGroupItem
                            value={TICKETS_INTERVAL.WEEKLY as any}
                            id={'interval-weekly'}
                          />
                          <label htmlFor={'interval-weekly'} className="cursor-pointer">
                            <p className="font-medium text-sm text-ebony opacity-50 hover:opacity-100 transition">
                              Weekly
                            </p>
                          </label>
                        </div>
                        <div className="flex items-center space-x-[7px] space-y-0">
                          <RadioGroupItem
                            value={TICKETS_INTERVAL.MONTHLY as any}
                            id={'interval-monthly'}
                          />
                          <label htmlFor={'interval-monthly'} className="cursor-pointer">
                            <p className="font-medium text-sm text-ebony opacity-50 hover:opacity-100 transition">
                              Monthly
                            </p>
                          </label>
                        </div>
                      </RadioGroup>
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={'flex flex-col'}>
          <div className={'flex flex-col gap-2.5'}>
            <p className={'text-sm text-ebony_opacity_80 font-semibold'}>API Details</p>
          </div>
          <div className={'flex flex-col gap-2.5'}>
            <div className={'flex flex-col gap-[5px] mt-2.5'}>
              <Controller
                control={control}
                name="subdomain"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    label={'Freshdesk subdomain*'}
                    placeholder={'domain.com'}
                    className={'!py-2.5 !px-3.5'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={(e) => {
                      const res = getSubdomainHelper(e.target.value);
                      if (res?.subdomain) {
                        onChange(res?.subdomain);
                      } else {
                        onChange(e.target.value);
                      }
                    }}
                    infoTooltip={{
                      side: 'right',
                      description: 'Link to your Freshdesk workspace.',
                    }}
                  />
                )}
              />
            </div>
            <div className={'flex flex-col gap-[5px]'}>
              <Controller
                control={control}
                name="api_key"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'password'}
                    label={'Freshdesk API Key'}
                    className={'!py-2.5 !px-3.5'}
                    placeholder={'------------------------------------------------------'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    infoTooltip={{
                      side: 'right',
                      description: (
                        <>
                          Check how to generate the API Key{' '}
                          <a
                            href={FRESHDESK_URL.HOW_TO_FIND_YOUR_API_KEY}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline cursor-pointer"
                          >
                            here →
                          </a>
                        </>
                      ),
                    }}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center mt-5">
        <div className="flex gap-2.5">
          <Button variant="outline" type="button" className="min-w-24" onClick={() => close()}>
            Cancel
          </Button>
          <Button type="submit" className="min-w-24">
            <span>Save</span>
          </Button>
        </div>
      </div>
    </form>
  );
};
